import { useNavigationStore } from 'store';
import Button from '@mui/material/Button';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import DeviceManager from '@/components/util/deviceManager';
import { Stack } from '@mui/material';
import { FAIcon } from '@packages/ui-components';

export const SortMenu = ({
  rtl,
  sortingValue,
  setSortingValue,
}: {
  rtl: boolean;
  sortingValue: string;
  setSortingValue: Dispatch<SetStateAction<string>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sortingValueOptions = useMemo(
    () => [
      {
        value: 'default',
        label: translation?.navDefault || 'Default',
        icon: <i className="fa-thin fa-sort" />,
      },
      {
        value: 'alphabetical',
        label: translation?.navAlphabetical || 'A-Z',
        icon: (
          <FAIcon
            icon="faArrowUpArrowDown"
            style={{
              transform: 'rotateX(180deg)',
            }}
          />
        ),
      },
      {
        value: 'reverseAlphabetical',
        label: translation?.navReverseAlphabetical || 'Z-A',
        icon: <FAIcon icon="faArrowUpArrowDown" />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rtl],
  );

  return (
    <>
      <Button
        id="sort-button"
        aria-controls={open ? 'sorting-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        sx={{
          height: { xs: DeviceManager.isMobile() ? '36px' : '44px', lg: '44px' },
          width: { xs: DeviceManager.isMobile() ? '36px' : 'auto', lg: 'auto' },
          borderRadius: { xs: DeviceManager.isMobile() ? '30px' : '8px', sm: '8px' },
          minWidth: { xs: DeviceManager.isMobile() ? '36px' : '64px', sm: '64px' },
          border: open ? '1px solid #2055FF' : '1px solid #EAECF0',
          background: open ? 'rgba(32, 85, 255, 0.06)' : '#FFF',
          color: open ? '#2055FF' : '#000',
          direction: rtl ? 'rtl' : 'ltr',
          gap: '8px',
          '&:hover': {
            background: 'rgba(32, 85, 255, 0.06)',
            color: open ? '#2055FF' : '#000',
          },
          '&:focus': {
            border: '1px solid #2055FF',
            background: 'rgba(32, 85, 255, 0.06)',
          },
          '.MuiButton-startIcon': {
            marginRight: { xs: 0, sm: '8px' },
            marginLeft: { xs: 0, sm: '-4px' },
          },
        }}
        startIcon={
          DeviceManager.isMobile() ? (
            <FAIcon
              icon="faBarsFilter"
              size={16}
              color="#6B6B6B"
            />
          ) : (
            <FAIcon
              icon="faSort"
              size={16}
              color="#667085"
              style={{
                width: '20px',
                height: '20px',
                color: '#667085',
              }}
            />
          )
        }
      >
        <Stack direction="row">
          <Typography
            fontSize="16px"
            fontWeight={400}
            sx={{ display: { xs: DeviceManager.isMobile() ? 'none' : 'initial', sm: 'initial' } }}
          >
            {translation?.navSortBy || 'Sort By'}
          </Typography>
          <Typography
            fontSize="16px"
            fontWeight={400}
            sx={{ display: { xs: 'none', sm: 'initial' } }}
          >
            :{' '}
            {sortingValueOptions.find((option) => option.value === sortingValue)?.label ||
              translation?.navDefault ||
              'Default'}
          </Typography>
        </Stack>
      </Button>
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        id="sorting-menu"
        sx={{
          '.MuiPopover-paper': {
            width: '203px',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            mt: '10px',
          },
          direction: rtl ? 'rtl' : 'ltr',
        }}
        MenuListProps={{
          'aria-labelledby': 'sort-button',
          sx: {
            background: '#fff',
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {sortingValueOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setSortingValue(option.value);
              handleClose();
            }}
            sx={{
              display: 'inline-flex',
              justifyContent: 'flex-start',
              width: '100%',
              gap: '8px',
              ':hover': {
                background: 'rgba(32, 85, 255, 0.06)',
              },
            }}
          >
            {option.icon}
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
